import { useEffect, useState, useCallback } from "react";
import { Target, Activity, Layers, CheckCircle2, Circle } from "lucide-react";
import { getBotPlans, fmtUsd } from "@/lib/api";

const rsiTone = (rsi) => {
  if (rsi == null) return { label: "—", color: "#71717A" };
  if (rsi <= 30) return { label: "Aşırı Satım", color: "#00FFA3" };
  if (rsi <= 45) return { label: "Zayıf", color: "#8FE388" };
  if (rsi >= 70) return { label: "Aşırı Alım", color: "#FF3366" };
  return { label: "Nötr", color: "#A1A1AA" };
};

const price = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 6 })}`);

export const StrategyPanel = () => {
  const [plans, setPlans] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try { setPlans(await getBotPlans()); } catch { /* ignore */ }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-5" data-testid="strategy-panel">
      <div className="flex items-center gap-2 mb-4">
        <Target className="h-4 w-4 text-[var(--pos)]" />
        <h3 className="font-head font-bold text-sm">Kademeli Alım Planları</h3>
        <span className="ml-auto text-[10px] font-mono text-[var(--text-faint)]">{plans.length} aktif</span>
      </div>

      {plans.length === 0 ? (
        <div className="py-8 text-center text-xs text-[var(--text-faint)]">
          {loaded ? "Aktif kademeli alım planı yok. Bot uygun destek noktası bulduğunda plan burada görünür."
                  : "Yükleniyor..."}
        </div>
      ) : (
        <div className="space-y-4">
          {plans.map((p) => {
            const tone = rsiTone(p.rsi);
            return (
              <div key={p.symbol} data-testid={`plan-${p.symbol}`}
                className="rounded-lg border border-[var(--border)] bg-[var(--app-bg)] p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-head font-bold text-[var(--text-primary)]">{p.symbol}</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                      style={{ background: `${tone.color}1A`, color: tone.color }}>
                      RSI {p.rsi ?? "—"} · {tone.label}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-mono text-[var(--text-primary)]">{price(p.current_price)}</div>
                    <div className="text-[10px] text-[var(--text-faint)] flex items-center gap-1 justify-end">
                      <Activity className="h-3 w-3" /> Oynaklık %{((p.volatility || 0) * 100).toFixed(1)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-2 text-[10px] text-[var(--text-dim)]">
                  <Layers className="h-3 w-3 text-[var(--accent)]" />
                  <span>Bütçe {fmtUsd(p.total_alloc)}</span>
                  {p.avg_price && <span>· Ort. Maliyet {price(p.avg_price)}</span>}
                  <span className="ml-auto font-mono text-[var(--pos)]">{p.filled_count}/{p.total_count} kademe dolduruldu</span>
                </div>

                <div className="space-y-1.5">
                  {(p.buy_levels || []).map((lv, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      {lv.filled
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-[var(--pos)]" />
                        : <Circle className="h-3.5 w-3.5 text-[var(--border-strong)]" />}
                      <span className={lv.filled ? "text-[var(--pos)]" : "text-[var(--text-dim)]"}>
                        Kademe {i + 1}
                      </span>
                      <span className="font-mono text-[var(--text-muted)]">{price(lv.price)}</span>
                      <span className="ml-auto font-mono text-[var(--text-faint)]">%{Math.round(lv.frac * 100)}</span>
                      <span className={`text-[10px] ${lv.filled ? "text-[var(--pos)]" : "text-[var(--text-faint)]"}`}>
                        {lv.filled ? "alındı" : "bekliyor"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
